/**
 * Sidebar Component
 */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Divider,
    IconButton,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    MedicalServices as ServicesIcon,
    Category as SubServicesIcon,
    Layers as InternalSubServicesIcon,
    ShoppingCart as OrdersIcon,
    People as UsersIcon,
    Person as SpecialistIcon,
    SupervisorAccount as ConsultantIcon,
    Payment as PaymentsIcon,
    Assessment as ReportsIcon,
    Settings as SettingsIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Close as CloseIcon,
    Campaign as AdsIcon,
    Description as TermsIcon,
    Security as RolesIcon,
    Badge as EmployeesIcon,
    Inventory2 as ProductsIcon,
    CategoryOutlined as CategoriesIcon,
    AccountTree as SubCategoriesIcon,
    StarRate as ReviewsIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { toggleSidebarCollapsed, setSidebarOpen } from '../../store/slices/uiSlice';
import { palette } from '../../theme/palette';
import { usePermissions } from '../../hooks/usePermissions';
import {
    NAV_DRAWER_WIDTH,
    NAV_DRAWER_WIDTH_COLLAPSED,
    MOBILE_NAV_BREAKPOINT,
} from './layoutConstants';

const DRAWER_WIDTH = NAV_DRAWER_WIDTH;
const COLLAPSED_WIDTH = NAV_DRAWER_WIDTH_COLLAPSED;

interface MenuItem {
    title: string;
    path: string;
    icon: React.ReactNode;
    permission?: string; // Permission code required to view this menu item
    anyPermission?: string[]; // Visible if the user has ANY of these permissions
}

interface MenuSection {
    title: string;
    items: MenuItem[];
}

// `title` holds an i18n key (nav.*), resolved with t() at render time.
const menuSections: MenuSection[] = [
    {
        title: 'nav.main',
        items: [
            { title: 'nav.dashboard', path: '/', icon: <DashboardIcon />, permission: 'view_dashboard' },
            { title: 'nav.ads', path: '/ads', icon: <AdsIcon />, permission: 'view_ads' },
            { title: 'nav.terms', path: '/terms', icon: <TermsIcon />, permission: 'view_ads' },
        ],
    },
    {
        title: 'nav.services',
        items: [
            { title: 'nav.services', path: '/services', icon: <ServicesIcon />, permission: 'view_services' },
            { title: 'nav.subServices', path: '/subservices', icon: <SubServicesIcon />, permission: 'view_services' },
            { title: 'nav.internalSubServices', path: '/internal-subservices', icon: <InternalSubServicesIcon />, permission: 'view_services' },
        ],
    },
    {
        title: 'nav.marketplace',
        items: [
            { title: 'nav.categories', path: '/marketplace/categories', icon: <CategoriesIcon />, permission: 'view_marketplace' },
            { title: 'nav.subCategories', path: '/marketplace/subcategories', icon: <SubCategoriesIcon />, permission: 'view_marketplace' },
            { title: 'nav.products', path: '/marketplace/products', icon: <ProductsIcon />, permission: 'view_marketplace' },
        ],
    },
    {
        title: 'nav.people',
        items: [
            { title: 'nav.patients', path: '/users', icon: <UsersIcon />, permission: 'view_patients' },
            { title: 'nav.specialists', path: '/specialists', icon: <SpecialistIcon />, permission: 'view_specialists' },
            { title: 'nav.consultants', path: '/consultants', icon: <ConsultantIcon />, permission: 'view_consultants' },
        ],
    },
    {
        title: 'nav.finance',
        items: [
            // These pages are Services/Marketplace tab shells (OrdersTabsPage,
            // PaymentsTabsPage) — they gate each tab themselves, so either
            // permission is enough to warrant showing the entry.
            { title: 'nav.orders', path: '/orders', icon: <OrdersIcon />, anyPermission: ['view_orders', 'view_marketplace'] },
            { title: 'nav.payments', path: '/payments', icon: <PaymentsIcon />, anyPermission: ['view_payments', 'view_marketplace'] },
            { title: 'nav.reviews', path: '/reviews', icon: <ReviewsIcon />, permission: 'view_reviews' },
            { title: 'nav.reports', path: '/reports', icon: <ReportsIcon />, permission: 'view_reports' },
        ],
    },
    {
        title: 'nav.system',
        items: [
            { title: 'nav.roles', path: '/roles', icon: <RolesIcon />, permission: 'view_roles' },
            { title: 'nav.employees', path: '/employees', icon: <EmployeesIcon />, permission: 'view_employees' },
            { title: 'nav.settings', path: '/settings', icon: <SettingsIcon />, permission: 'view_settings' },
        ],
    },
];

const Sidebar = () => {
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { sidebarCollapsed, sidebarOpen } = useAppSelector((state) => state.ui);
    const { hasPermission, isSuperUser } = usePermissions();
    const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_NAV_BREAKPOINT));

    const effectiveCollapsed = isMobile ? false : sidebarCollapsed;

    const prevIsMobile = useRef<boolean | undefined>(undefined);
    useEffect(() => {
        if (prevIsMobile.current === false && isMobile) {
            dispatch(setSidebarOpen(false));
        }
        prevIsMobile.current = isMobile;
    }, [isMobile, dispatch]);

    const isActive = (path: string) => {
        if (path === '/') return location.pathname === '/';
        return location.pathname.startsWith(path);
    };

    // Filter menu items based on permissions
    const filterMenuItems = (items: MenuItem[]) => {
        return items.filter(item => {
            // If no permission is specified, show the item
            if (!item.permission && !item.anyPermission) return true;

            // SuperUsers see everything
            if (isSuperUser()) return true;

            // Any-of permissions: visible if the user has at least one
            if (item.anyPermission) {
                return item.anyPermission.some(p => hasPermission(p));
            }

            // Check if user has the required permission
            return hasPermission(item.permission!);
        });
    };

    const drawerWidth = effectiveCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

    const closeMobileNav = () => {
        if (isMobile) dispatch(setSidebarOpen(false));
    };

    const handleNav = (path: string) => {
        navigate(path);
        closeMobileNav();
    };

    const paperSx = {
        width: drawerWidth,
        boxSizing: 'border-box' as const,
        backgroundColor:
            theme.palette.mode === 'dark'
                ? palette.sidebar.dark.background
                : palette.sidebar.light.background,
        borderRight: `1px solid ${theme.palette.divider}`,
        overflowX: 'hidden' as const,
    };

    return (
        <Drawer
            variant={isMobile ? 'temporary' : 'permanent'}
            open={isMobile ? sidebarOpen : true}
            onClose={closeMobileNav}
            ModalProps={{ keepMounted: true }}
            sx={{
                width: { xs: 0, md: drawerWidth },
                flexShrink: 0,
                '& .MuiDrawer-paper': paperSx,
            }}
        >
            {/* Logo */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: effectiveCollapsed ? 'column' : 'row',
                    justifyContent: effectiveCollapsed ? 'center' : 'space-between',
                    gap: effectiveCollapsed ? 1 : 1.5,
                    px: effectiveCollapsed ? 1 : 2,
                    py: 2,
                    minHeight: 64,
                }}
            >
                {effectiveCollapsed ? (
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '12px',
                            bgcolor: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            border: `1px solid ${theme.palette.divider}`,
                            boxShadow: '0 4px 12px rgba(15, 76, 129, 0.12)',
                        }}
                    >
                        <Box
                            component="img"
                            src="/logo.jpg"
                            alt={t('brand.name')}
                            sx={{ width: 36, height: 36, objectFit: 'contain', display: 'block' }}
                        />
                    </Box>
                ) : (
                    <Typography
                        noWrap
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            px: 0.5,
                            fontSize: '1.5rem',
                            fontWeight: 800,
                            lineHeight: 1.6,
                            background: `linear-gradient(180deg, ${palette.brand[500]} 0%, ${palette.brandSecondary[500]} 100%)`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        {t('brand.name')}
                    </Typography>
                )}
                <IconButton
                    size="small"
                    onClick={() =>
                        isMobile ? closeMobileNav() : dispatch(toggleSidebarCollapsed())
                    }
                    sx={{ color: theme.palette.text.secondary }}
                    aria-label={isMobile ? 'Close menu' : 'Toggle sidebar'}
                >
                    {isMobile ? (
                        <CloseIcon />
                    ) : effectiveCollapsed ? (
                        <ChevronRightIcon />
                    ) : (
                        <ChevronLeftIcon />
                    )}
                </IconButton>
            </Box>

            <Divider />

            {/* Menu */}
            <Box sx={{ py: 1, flex: 1, overflowY: 'auto' }}>
                {menuSections.map((section, sectionIndex) => {
                    // Filter items in this section based on permissions
                    const visibleItems = filterMenuItems(section.items);

                    // Don't show section if no items are visible
                    if (visibleItems.length === 0) return null;

                    return (
                        <Box key={section.title} sx={{ mb: 1 }}>
                            {!effectiveCollapsed && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        px: 3,
                                        py: 1,
                                        display: 'block',
                                        color: theme.palette.text.secondary,
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    {t(section.title)}
                                </Typography>
                            )}
                            <List disablePadding>
                                {visibleItems.map((item) => {
                                    const active = isActive(item.path);
                                    return (
                                        <ListItem key={item.path} disablePadding sx={{ px: 1 }}>
                                            <ListItemButton
                                                onClick={() => handleNav(item.path)}
                                                sx={{
                                                    borderRadius: 2,
                                                    mb: 0.5,
                                                    minHeight: 44,
                                                    justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
                                                    px: effectiveCollapsed ? 1.5 : 2,
                                                    ...(active && {
                                                        background: `linear-gradient(180deg, ${palette.brand[500]} 0%, ${palette.brandSecondary[500]} 100%)`,
                                                        color: '#FFFFFF',
                                                        '& .MuiListItemIcon-root': {
                                                            color: '#FFFFFF',
                                                        },
                                                    }),
                                                    ...(!active && {
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.mode === 'dark'
                                                                ? 'rgba(59, 193, 207, 0.15)'
                                                                : 'rgba(59, 193, 207, 0.1)',
                                                        },
                                                    }),
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={{
                                                        minWidth: effectiveCollapsed ? 0 : 40,
                                                        justifyContent: 'center',
                                                        color: active ? '#FFFFFF' : theme.palette.text.secondary,
                                                    }}
                                                >
                                                    {item.icon}
                                                </ListItemIcon>
                                                {!effectiveCollapsed && (
                                                    <ListItemText
                                                        primary={t(item.title)}
                                                        primaryTypographyProps={{
                                                            fontSize: '0.875rem',
                                                            fontWeight: active ? 600 : 500,
                                                        }}
                                                    />
                                                )}
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            </List>
                            {sectionIndex < menuSections.length - 1 && !effectiveCollapsed && visibleItems.length > 0 && (
                                <Divider sx={{ my: 1, mx: 2 }} />
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Drawer>
    );
};

export default Sidebar;

